import {
  EDITION,
  MAX_SAVED_IMAGE_STYLES,
  PER_PAGE_10
} from '@/common/constants';
import { get } from 'lodash';
import { managerQuery } from '../book/queries';
import { getSheetFramesQuery } from '../frame/queries';
import {
  getPrintSettingsQuery,
  getDigitalSettingsQuery,
  portraitFoldersSelectedQuery,
  sheetPortraitQuery
} from '../portrait/queries';
import { digitalWorkspaceQuery, printWorkspaceQuery } from '../sheet/queries';
import { getFavoriteIdsQuery } from '../user/queries';
import { getPresetColorPickerQuery } from '../util/queries';
import {
  getUserDigitalLayoutsQuery,
  getUserLayoutsQuery
} from '../layout/queries';
import { getUserTextStyleQuery } from '../text/queries';
import { getUserImageStyleQuery } from '../image/queries';
import {
  getAlbumCategoryQuery,
  getCommunityAlbumsQuery,
  getInProjectAssetsQuery,
  getUserAlbumsQuery,
  getUserAvailableAlbumsQuery,
  getPageInProjectAssetsQuery,
  getDigitalFrameInProjectAssetsQuery
} from '../media/queries';

import { getMappingSettingsQuery } from '../mapping/queries';
import { getSectionTagsQuery } from '../section/queries';
import {
  getUserBackgroundQuery,
  getUserClipArtQuery
} from '../generateAI/queries';

export const updatePortraitSettingCache = (result, args, cache) => {
  const layoutType = get(args, 'portrait_layout_setting_params.layout_type');
  const isDigital = layoutType === EDITION.PRINT.toUpperCase();
  const layout = isDigital
    ? 'print_portrait_layout_settings'
    : 'digital_portrait_layout_settings';

  const query = isDigital ? getPrintSettingsQuery : getDigitalSettingsQuery;

  cache.updateQuery(
    {
      query,
      variables: { bookId: args.book_id }
    },
    data => {
      data.book[layout].push(result.create_portrait_layout_setting);

      return data;
    }
  );
};

export const updateTemplateUserCache = (result, _, cache) => {
  const template = get(result, 'create_template_user.template', {});

  const pageTemplateFavourites = cache
    .inspectFields({ __typename: 'Query' })
    .filter(
      cacheInfo =>
        cacheInfo.fieldName === 'template_favourites' &&
        cacheInfo.arguments?.page
    )
    .map(cacheInfo => cacheInfo.arguments.page);
  pageTemplateFavourites.forEach(page => {
    cache.invalidate({ __typename: 'Query' }, 'template_favourites', {
      page: page,
      per_page: PER_PAGE_10
    });
  });

  cache.updateQuery(
    {
      query: getFavoriteIdsQuery
    },
    data => {
      if (data) data.template_favourites.push(template);

      return data;
    }
  );
};

export const updateDeleteTemplateUser = (result, _, cache) => {
  const templateId = get(result, 'delete_template_user.template.id', null);

  const pageTemplateFavourites = cache
    .inspectFields({ __typename: 'Query' })
    .filter(
      cacheInfo =>
        cacheInfo.fieldName === 'template_favourites' &&
        cacheInfo.arguments?.page
    )
    .map(cacheInfo => cacheInfo.arguments.page);
  pageTemplateFavourites.forEach(page => {
    cache.invalidate({ __typename: 'Query' }, 'template_favourites', {
      page: page,
      per_page: PER_PAGE_10
    });
  });

  cache.updateQuery(
    {
      query: getFavoriteIdsQuery
    },
    data => {
      if (data) {
        data.template_favourites = data.template_favourites.filter(
          template => template.id !== templateId
        );
      }
      return data;
    }
  );
};

export const updateBookCollectionCache = (result, args, cache) => {
  const collectionId = get(
    result,
    'create_books_portrait_collections.id',
    null
  );

  const bookId = args.books_portrait_collections_params.book_id;

  cache.updateQuery(
    {
      query: portraitFoldersSelectedQuery,
      variables: { bookId }
    },
    data => {
      if (data) {
        data.books_portrait_collections_by_book.push({
          portrait_collection: { id: collectionId }
        });
      }

      return data;
    }
  );
};

export const updateSheetCache = (_, args, cache) => {
  const digitalWorkspace = get(args, 'sheet_params.digital_workspace', null);
  const printWorkspace = get(args, 'sheet_params.workspace', null);

  const updateCache = (query, prefix, assets) => {
    cache.updateQuery(
      {
        query,
        variables: { id: args.sheet_id }
      },
      data => {
        if (data) data.sheet[`${prefix}workspace`][`${prefix}assets`] = assets;

        return data;
      }
    );
  };
  if (digitalWorkspace) {
    const assets = get(JSON.parse(digitalWorkspace), 'digital_assets', []);
    updateCache(digitalWorkspaceQuery, 'digital_', assets);
  }
  if (printWorkspace) {
    const assets = get(JSON.parse(printWorkspace), 'assets', []);
    updateCache(printWorkspaceQuery, '', assets);
  }
};

export const updateCreateFrame = (results, args, cache) => {
  const frame = get(results, 'create_digital_frame', null);
  const { sheet_id: sheetId } = args;

  if (!sheetId) return;

  cache.updateQuery(
    {
      query: getSheetFramesQuery,
      variables: { sheetId }
    },
    data => {
      if (data)
        data.sheet.digital_frames.push({
          ...frame,
          __typename: 'DigitalFrame'
        });

      return data;
    }
  );
};

export const updateDeleteFrame = (_, args, cache) => {
  const frameId = args.digital_frame_id;

  const sheetIds = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'sheet')
    .map(cacheInfo => String(cacheInfo.arguments.id));

  const bookId = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'book')
    .map(cacheInfo => String(cacheInfo.arguments.id));

  cache.invalidate({ __typename: 'Query' }, 'book', { id: bookId[0] });

  let sheetId;
  let counter = 0;

  // getting sheet id
  while (!sheetId && counter < sheetIds.length) {
    const queryData = cache.readQuery({
      query: getSheetFramesQuery,
      variables: { sheetId: sheetIds[counter] }
    });
    if (!queryData) return;

    const sheet = queryData.sheet;

    const isIncludedTheFrame = sheet.digital_frames.some(
      frame => frame.id === frameId
    );
    if (isIncludedTheFrame) sheetId = sheet.id;
    counter++;
  }

  if (!frameId || !sheetId) return;

  cache.updateQuery(
    {
      query: getSheetFramesQuery,
      variables: { sheetId: sheetId }
    },
    data => {
      if (data) {
        // remove deleted frames
        data.sheet.digital_frames = data.sheet.digital_frames.filter(
          f => f.id !== frameId
        );

        // remove the last transition
        data.sheet.digital_transitions.pop();
      }
      return data;
    }
  );
};

export const updateCreateSheet = (results, args, cache) => {
  const sheetId = get(results, 'create_sheet.id', null);
  const bookId = get(results, 'create_sheet.book.id', null);
  const { book_section_id, sheet_params } = args;

  if (!sheetId || !bookId || !book_section_id) return;

  cache.updateQuery(
    {
      query: managerQuery,
      variables: { bookId }
    },
    data => {
      if (data) {
        const sectionIndex = data.book.book_sections.findIndex(
          section => section.id === book_section_id
        );
        data.book.book_sections[sectionIndex].sheets.push({
          ...sheet_params,
          id: sheetId,
          __typename: 'Sheet'
        });
      }

      return data;
    }
  );
};

const getIndexOfSheetSection = (sections, sheetId) => {
  let sheetIndex = null;
  const sectionIndex = sections.findIndex(section =>
    section.sheets.some((sheet, idx) => {
      if (sheet.id === sheetId) {
        sheetIndex = idx;
        return true;
      }
    })
  );

  return { sheetIndex, sectionIndex };
};

export const updateDeleteSheet = (results, _, cache) => {
  const sheetId = get(results, 'delete_sheet.id', null);
  const bookId = get(results, 'delete_sheet.book.id', null);
  const assetParams = get(results, 'delete_sheet.book.in_project_assets', null);

  if (!sheetId || !bookId) return;

  cache.updateQuery(
    {
      query: managerQuery,
      variables: { bookId }
    },
    data => {
      if (data) {
        const sections = data.book.book_sections;

        const { sheetIndex, sectionIndex } = getIndexOfSheetSection(
          sections,
          sheetId
        );

        sections[sectionIndex].sheets.splice(sheetIndex, 1);
      }

      return data;
    }
  );
  // reset in project mark
  cache.updateQuery(
    {
      query: getInProjectAssetsQuery,
      variables: { bookId: bookId }
    },
    data => {
      if (data && assetParams) {
        data.book.in_project_assets = assetParams;
      }
      return data;
    }
  );
};

export const updateDeleteSection = (results, _, cache) => {
  const sectionId = get(results, 'delete_book_section.id', null);
  const bookId = get(results, 'delete_book_section.book.id', null);
  const assetParams = get(
    results,
    'delete_book_section.book.in_project_assets',
    null
  );

  if (!sectionId || !bookId) return;

  cache.updateQuery(
    {
      query: managerQuery,
      variables: { bookId }
    },
    data => {
      if (data) {
        const sections = data.book.book_sections;

        const sectionIndex = sections.findIndex(
          section => section.id === sectionId
        );

        sections.splice(sectionIndex, 1);
      }
      return data;
    }
  );

  // reset in project mark
  cache.updateQuery(
    {
      query: getInProjectAssetsQuery,
      variables: { bookId: bookId }
    },
    data => {
      if (data && assetParams) {
        data.book.in_project_assets = assetParams;
      }
      return data;
    }
  );
};

export const updateCreateSection = (results, args, cache) => {
  const sectionParams = get(results, 'create_book_section', null);
  const { book_id: bookId } = args;

  if (!bookId) return;

  cache.updateQuery(
    {
      query: managerQuery,
      variables: { bookId }
    },
    data => {
      if (data) {
        data.book.book_sections.push({
          ...sectionParams
        });
      }
      return data;
    }
  );
};

export const updateSectionCache = (results, args, cache) => {
  const sectionId = get(results, 'update_book_section.id', null);
  const bookId = get(results, 'update_book_section.book.id', null);

  const isUpdateAssignee = Object.prototype.hasOwnProperty.call(
    args.book_section_params,
    'assigned_user_id'
  );
  const newTags = get(results, 'update_book_section.tags', null);

  cache.updateQuery(
    {
      query: getSectionTagsQuery,
      variables: { id: sectionId }
    },
    data => {
      if (data && newTags) {
        data.book_section.tags = newTags;
      }
      return data;
    }
  );

  if (!sectionId || !bookId || !isUpdateAssignee) return;

  cache.invalidate({ __typename: 'Query' }, 'book', { id: bookId });
};

export const moveSheetCache = (results, args, cache) => {
  const bookId = get(results, 'move_sheet[0].book.id');
  const {
    target_book_section_id: sectionId,
    target_placement: targetIndex,
    sheet_id: sheetId
  } = args;

  if (!bookId || !sheetId) return;
  cache.updateQuery(
    {
      query: managerQuery,
      variables: { bookId }
    },
    data => {
      // remove sheet from its original section
      const sections = data.book.book_sections;

      const { sheetIndex, sectionIndex } = getIndexOfSheetSection(
        sections,
        sheetId
      );

      const targetSheet = sections[sectionIndex].sheets.splice(
        sheetIndex,
        1
      )[0];

      // insert sheet to target section
      const targetSectionIndex = sections.findIndex(
        section => section.id === sectionId
      );
      sections[targetSectionIndex].sheets.splice(targetIndex, 0, targetSheet);

      return data;
    }
  );
};

export const updatePresentColorPickerCache = (_, args, cache) => {
  const colors = args.colors;

  cache.updateQuery({ query: getPresetColorPickerQuery }, data => {
    data.user_favourite_colors = colors;
    return data;
  });
};

export const createUserCustomPrintTemplate = (results, args, cache) => {
  const layout = get(results, 'create_user_custom_print_template', null);
  const saveType = args?.save_type;
  const saveTypeMapping = {
    PAGE: 'SINGLE_PAGE',
    SHEET: 'DOUBLE_PAGE'
  };
  const dimensionIds = cache
    .inspectFields({ __typename: 'Query' })
    .filter(
      cacheInfo =>
        cacheInfo.fieldName === 'user_saved_print_layouts_by_dimension_id'
    )
    .map(cacheInfo => String(cacheInfo.arguments.dimension_id));

  const category = saveTypeMapping[saveType];
  cache.updateQuery(
    {
      query: getUserLayoutsQuery,
      variables: {
        layoutType: category,
        page: 1,
        perPages: 10,
        dimensionId: dimensionIds[0]
      }
    },
    data => {
      if (data && layout)
        data.user_saved_print_layouts_by_dimension_id.push({
          ...layout,
          template_element_mappings: []
        });

      return data;
    }
  );
};

export const createUserCustomDigitalTemplateCache = (results, _, cache) => {
  const layout = get(results, 'create_user_custom_digital_template');

  cache.updateQuery(
    { query: getUserDigitalLayoutsQuery, variables: { page: 1, perPages: 10 } },
    data => {
      if (data && layout) data.user_saved_digital_layouts.push(layout);

      return data;
    }
  );
};

export const updateTextStyle = (results, _, cache) => {
  const style = get(results, 'create_text_style');
  cache.updateQuery({ query: getUserTextStyleQuery }, data => {
    if (data && style) data.user_text_styles.push(style);

    return data;
  });
};

export const updateImageStyle = (res, _, cache) => {
  const style = get(res, 'create_image_style');
  cache.updateQuery(
    {
      query: getUserImageStyleQuery,
      variables: { page: 1, perPages: MAX_SAVED_IMAGE_STYLES }
    },
    data => {
      if (data && style) {
        data.user_image_styles.unshift(style);
        data.user_image_styles.pop();
      }

      return data;
    }
  );
};

export const createContainerCache = (res, args, cache) => {
  const album = res.create_container;
  const communityId = get(args, 'container_params.community_id');

  if (!album) return;

  // update user containers query
  cache.updateQuery(
    { query: getUserAlbumsQuery, variables: { page: 1 } },
    data => {
      if (data) {
        data.user_containers?.unshift(album);
      }
      return data;
    }
  );
  // update category
  cache.updateQuery(
    { query: getAlbumCategoryQuery, variables: { communityId, page: 1 } },
    data => {
      if (data) {
        const category = {
          id: album.id,
          body: album.body,
          __typename: album.__typename
        };
        data.community_containers?.unshift(category);
        data.user_containers.unshift(album);
      }

      return data;
    }
  );

  // update community containers query
  cache.updateQuery(
    {
      query: getCommunityAlbumsQuery,
      variables: { communityId, page: 1 }
    },
    data => {
      if (data) data.community_containers?.unshift(album);

      return data;
    }
  );

  // update user available albums query
  cache.updateQuery(
    {
      query: getUserAvailableAlbumsQuery,
      variables: { page: 1, perPages: 100 }
    },
    data => {
      if (data) data.user_available_containers.push(album);
      return data;
    }
  );
};

export const updateProjectMappingConfig = (res, args, cache) => {
  const config = res.update_project_mapping_configuration;
  const bookId = args.book_id;

  cache.updateQuery(
    { query: getMappingSettingsQuery, variables: { bookId } },
    data => {
      if (data && !data.book.project_mapping_configuration) {
        data.book.project_mapping_configuration = config;
      }
      return data;
    }
  );
};

/**
 * To delete all cache relate to layout
 */
export const invalidateLayoutMapping = (_, __, cache) => {
  const pageThemes = cache
    .inspectFields({ __typename: 'Query' })
    .filter(
      cacheInfo => cacheInfo.fieldName === 'themes' && cacheInfo.arguments?.page
    )
    .map(cacheInfo => cacheInfo.arguments.page);
  pageThemes.forEach(page => {
    cache.invalidate({ __typename: 'Query' }, 'themes', {
      page: page,
      per_page: PER_PAGE_10
    });
  });

  const pageTemplateBookPairs = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'template_book_pairs')
    .map(cacheInfo => cacheInfo.arguments.page);
  pageTemplateBookPairs.forEach(page => {
    cache.invalidate({ __typename: 'Query' }, 'template_book_pairs', {
      page: page,
      per_page: PER_PAGE_10
    });
  });

  const pageTemplateFavourites = cache
    .inspectFields({ __typename: 'Query' })
    .filter(
      cacheInfo =>
        cacheInfo.fieldName === 'template_favourites' &&
        cacheInfo.arguments?.page
    )
    .map(cacheInfo => cacheInfo.arguments.page);
  pageTemplateFavourites.forEach(page => {
    cache.invalidate({ __typename: 'Query' }, 'template_favourites', {
      page: page,
      per_page: PER_PAGE_10
    });
  });

  const pageUserDigitalLayouts = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'user_saved_digital_layouts')
    .map(cacheInfo => cacheInfo.arguments.page);
  pageUserDigitalLayouts.forEach(page => {
    cache.invalidate({ __typename: 'Query' }, 'user_saved_digital_layouts', {
      page: page,
      per_page: PER_PAGE_10
    });
  });

  const pageUserSpreadPrintLayouts = cache
    .inspectFields({ __typename: 'Query' })
    .filter(
      cacheInfo =>
        cacheInfo.fieldName === 'user_saved_print_layouts_by_dimension_id' &&
        cacheInfo.arguments.layout_type === 'DOUBLE_PAGE'
    )
    .map(cacheInfo => ({
      page: cacheInfo.arguments.page,
      dimensionId: cacheInfo.arguments.dimension_id
    }));
  pageUserSpreadPrintLayouts.forEach(({ page, dimensionId }) => {
    cache.invalidate(
      { __typename: 'Query' },
      'user_saved_print_layouts_by_dimension_id',
      {
        dimension_id: dimensionId,
        layout_type: 'DOUBLE_PAGE',
        page: page,
        per_page: PER_PAGE_10
      }
    );
  });

  const pageUserSinglePrintLayouts = cache
    .inspectFields({ __typename: 'Query' })
    .filter(
      cacheInfo =>
        cacheInfo.fieldName === 'user_saved_print_layouts_by_dimension_id' &&
        cacheInfo.arguments.layout_type === 'SINGLE_PAGE'
    )
    .map(cacheInfo => ({
      page: cacheInfo.arguments.page,
      dimensionId: cacheInfo.arguments.dimension_id
    }));
  pageUserSinglePrintLayouts.forEach(({ page, dimensionId }) => {
    cache.invalidate(
      { __typename: 'Query' },
      'user_saved_print_layouts_by_dimension_id',
      {
        dimension_id: dimensionId,
        layout_type: 'SINGLE_PAGE',
        page: page,
        per_page: PER_PAGE_10
      }
    );
  });

  const templateCategoryIds = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'templates')
    .map(cacheInfo => {
      const info = {
        categoryId: cacheInfo.arguments.category_id,
        page: cacheInfo.arguments.page
      };
      return info;
    });
  templateCategoryIds.forEach(({ categoryId, page }) => {
    cache.invalidate({ __typename: 'Query' }, 'templates', {
      category_id: categoryId,
      page: page,
      per_page: PER_PAGE_10
    });
  });

  const themeIds = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'theme')
    .map(cacheInfo => String(cacheInfo.arguments.id));

  const pairIds = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'template_book_pair')
    .map(cacheInfo => String(cacheInfo.arguments.id));

  themeIds.forEach(id =>
    cache.invalidate({ __typename: 'Query' }, 'theme', { id })
  );

  pairIds.forEach(id =>
    cache.invalidate({ __typename: 'Query' }, 'template_book_pair', { id })
  );
};

export const deletePortraitSheet = (res, _, cache) => {
  const sheetId = get(res, 'delete_portrait_sheet_setting.sheet.id');

  if (!sheetId) return;

  cache.updateQuery(
    {
      query: sheetPortraitQuery,
      variables: { sheetId }
    },
    data => {
      if (data) {
        data.sheet.portrait_sheet_setting = null;
      }
      return data;
    }
  );
};

export const createPortraitSheet = (res, args, cache) => {
  const sheetId = get(args, 'sheet_id');
  const setting = get(res, 'create_portrait_sheet_setting', null);

  cache.updateQuery(
    {
      query: sheetPortraitQuery,
      variables: { sheetId }
    },
    data => {
      if (data) {
        data.sheet.portrait_sheet_setting = setting;
      }
      return data;
    }
  );
};

export const deleteDigitalPortraitSheet = (res, _, cache) => {
  const sheetId = get(res, 'delete_digital_portrait_sheet_setting.sheet.id');

  if (!sheetId) return;

  cache.updateQuery(
    {
      query: sheetPortraitQuery,
      variables: { sheetId }
    },
    data => {
      if (data) {
        data.sheet.digital_portrait_sheet_setting = null;
      }
      return data;
    }
  );
};

export const createDigitalPortraitSheet = (res, args, cache) => {
  const sheetId = get(args, 'sheet_id');
  const setting = get(res, 'create_digital_portrait_sheet_setting', null);

  cache.updateQuery(
    {
      query: sheetPortraitQuery,
      variables: { sheetId }
    },
    data => {
      if (data) {
        data.sheet.digital_portrait_sheet_setting = setting;
      }
      return data;
    }
  );
};

export const createAssetCache = (res, args, cache) => {
  const projectId = get(args, 'designable_id');
  const type = get(args, 'designable_type');
  const assetParams = get(res, 'create_asset_designable');
  const bookId = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'book')
    .map(cacheInfo => String(cacheInfo.arguments.id));
  cache.updateQuery(
    {
      query: getInProjectAssetsQuery,
      variables: { bookId: bookId[0], projectId, type }
    },
    data => {
      if (data) {
        data.book.in_project_assets.push({ ...assetParams });
      }
      return data;
    }
  );
};

export const deleteAssetCache = (_, args, cache) => {
  const projectId = get(args, 'designable_id');
  const type = get(args, 'designable_type');
  const bookId = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'book')
    .map(cacheInfo => String(cacheInfo.arguments.id));
  const asset_id = get(args, 'asset_id');
  cache.updateQuery(
    {
      query: getInProjectAssetsQuery,
      variables: { bookId: bookId[0], projectId: +projectId, type }
    },
    data => {
      if (data) {
        const in_project_assets = data.book.in_project_assets;

        const assetIndex = in_project_assets.findIndex(
          asset => asset.id === asset_id
        );

        in_project_assets.splice(assetIndex, 1);
      }
      return data;
    }
  );
};

const updateInProjectAssets = (cache, query, variables, asset_ids) => {
  cache.updateQuery({ query, variables }, data => {
    if (data) {
      const in_project_assets =
        query === getPageInProjectAssetsQuery
          ? data.page.assets
          : data.digital_frame.assets;

      asset_ids.forEach(asset_id => {
        const assetIndex = in_project_assets.findIndex(
          asset => asset.id === asset_id
        );
        if (assetIndex >= 0) in_project_assets.splice(assetIndex, 1);
      });
    }
    return data;
  });
};

export const updateDeleteMultiAssetCache = (res, args, cache) => {
  const projectId = get(args, 'designable_id');
  const type = get(args, 'designable_type');
  const asset_ids = get(args, 'asset_ids');
  const assetParams = get(res, 'delete_assets_designables');

  const bookId = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'book')
    .map(cacheInfo => String(cacheInfo.arguments.id));

  cache.updateQuery(
    {
      query: getInProjectAssetsQuery,
      variables: { bookId: bookId[0] }
    },
    data => {
      if (data) {
        const in_project_assets = data.book.in_project_assets;
        assetParams.forEach(assetParam => {
          if (!assetParam.in_project) {
            const index = in_project_assets.findIndex(
              item => item.id === assetParam.id
            );
            if (index >= 0) in_project_assets.splice(index, 1);
          }
        });
      }
      return data;
    }
  );

  if (type === 'PAGE') {
    updateInProjectAssets(
      cache,
      getPageInProjectAssetsQuery,
      { pageId: projectId },
      asset_ids
    );
  } else if (type === 'DIGITAL_FRAME') {
    updateInProjectAssets(
      cache,
      getDigitalFrameInProjectAssetsQuery,
      { frameId: projectId },
      asset_ids
    );
  }
};

const addAssetsToCache = (cache, query, variables, assetParams) => {
  cache.updateQuery({ query, variables }, data => {
    if (data) {
      const in_project_assets =
        query === getPageInProjectAssetsQuery
          ? data.page.assets
          : data.digital_frame.assets;

      assetParams.forEach(assetParam => {
        in_project_assets.push({ ...assetParam });
      });
    }
    return data;
  });
};

export const updateCreateMultiAssetCache = (res, args, cache) => {
  const projectId = get(args, 'designable_id');
  const type = get(args, 'designable_type');
  const assetParams = get(res, 'create_bulk_asset_designables');
  const bookId = cache
    .inspectFields({ __typename: 'Query' })
    .filter(cacheInfo => cacheInfo.fieldName === 'book')
    .map(cacheInfo => String(cacheInfo.arguments.id));

  // Update in project assets for the book
  cache.updateQuery(
    {
      query: getInProjectAssetsQuery,
      variables: { bookId: bookId[0] }
    },
    data => {
      if (data) {
        const in_project_assets = data.book.in_project_assets;
        assetParams.forEach(assetParam => {
          const index = in_project_assets.findIndex(
            item => item.id === assetParam.id
          );
          if (index < 0) in_project_assets.push({ ...assetParam });
        });
      }
      return data;
    }
  );

  // Update specific type of assets
  if (type === 'PAGE') {
    addAssetsToCache(
      cache,
      getPageInProjectAssetsQuery,
      { pageId: projectId },
      assetParams
    );
  } else if (type === 'DIGITAL_FRAME') {
    addAssetsToCache(
      cache,
      getDigitalFrameInProjectAssetsQuery,
      { frameId: projectId },
      assetParams
    );
  }
};

export const deleteSectionTagCache = (res, args, cache) => {
  const sectionId = get(res, 'delete_book_section_tags.id', null);
  const tagId = args.tag_ids[0];
  cache.updateQuery(
    {
      query: getSectionTagsQuery,
      variables: { id: sectionId }
    },
    data => {
      if (data && data.book_section && tagId) {
        data.book_section.tags = data.book_section.tags.filter(
          tag => tag.id != tagId
        );
      }
      return data;
    }
  );
};

export const saveGeneratedBackgroundCache = (results, args, cache) => {
  const background = get(results, 'select_ai_generated_backgrounds', []);
  const pageType = background[0].page_type;
  cache.updateQuery(
    {
      query: getUserBackgroundQuery,
      variables: {
        pageType,
        page: 1,
        perPages: PER_PAGE_10
      }
    },
    data => {
      if (data && background)
        data.user_backgrounds_page_type.unshift(...background);

      return data;
    }
  );
};

export const saveGenerateClipartCache = (results, args, cache) => {
  const clipart = get(results, 'select_ai_generated_clipart', []);
  cache.updateQuery(
    {
      query: getUserClipArtQuery,
      variables: {
        page: 1,
        perPages: PER_PAGE_10
      }
    },
    data => {
      if (data && clipart) data.user_cliparts.unshift(...clipart);

      return data;
    }
  );
};
